import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Eye, EyeOff, Stethoscope, CheckCircle2, Search, Upload, ArrowRight } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { US_STATES } from "@/lib/us-states";
import { isValidNPI } from "@/lib/npi-validation";
import BAAgreement from "@/components/BAAgreement";
import { useRedirectIfAuthed } from "@/hooks/useRedirectIfAuthed";

const STEPS = ["Account", "Credentials", "BAA", "W-9"] as const;
type Step = (typeof STEPS)[number];

const ProviderRegister = () => {
  useRedirectIfAuthed();
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [showPassword, setShowPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    npiNumber: "",
    licenseNumber: "",
    licenseState: "",
    licenseExpiry: "",
    deaNumber: "",
    deaExpiry: "",
    specialty: "",
    bio: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [npiVerified, setNpiVerified] = useState(false);
  const [npiVerifying, setNpiVerifying] = useState(false);
  const [npiResult, setNpiResult] = useState<any>(null);
  const [baaAccepted, setBaaAccepted] = useState(false);
  const [baaLoading, setBaaLoading] = useState(false);
  const [w9File, setW9File] = useState<File | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [pendingConfirmation, setPendingConfirmation] = useState(false);
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const formatDateInput = (raw: string): string => {
    const digits = raw.replace(/\D/g, "").slice(0, 8);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  };

  const displayToIso = (display: string): string => {
    const match = display.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!match) return "";
    return `${match[3]}-${match[1]}-${match[2]}`;
  };

  const isoToDisplay = (iso: string): string => {
    const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return iso;
    return `${match[2]}/${match[3]}/${match[1]}`;
  };

  const updateField = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleDateField = (field: string, rawValue: string) => {
    const display = formatDateInput(rawValue);
    const iso = displayToIso(display);
    updateField(field, iso || display);
  };

  const validateAccount = (): boolean => {
    const errs: Record<string, string> = {};
    if (!formData.firstName.trim()) errs.firstName = "First name required";
    if (!formData.lastName.trim()) errs.lastName = "Last name required";
    if (!formData.email.trim()) errs.email = "Email required";
    if (!formData.password || formData.password.length < 6) errs.password = "Password must be at least 6 characters";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateCredentials = (): boolean => {
    const errs: Record<string, string> = {};
    if (!formData.npiNumber || !isValidNPI(formData.npiNumber)) errs.npiNumber = "Valid 10-digit NPI number required";
    if (!formData.licenseNumber.trim()) errs.licenseNumber = "Medical license number required";
    if (!formData.licenseState) errs.licenseState = "License state required";
    if (!formData.licenseExpiry) errs.licenseExpiry = "License expiration date required";
    else if (new Date(formData.licenseExpiry) <= new Date()) errs.licenseExpiry = "License must not be expired";
    if (formData.deaNumber && !formData.deaExpiry) errs.deaExpiry = "DEA expiration required when DEA number is provided";
    if (formData.deaExpiry && new Date(formData.deaExpiry) <= new Date()) errs.deaExpiry = "DEA must not be expired";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleAccountStep = async () => {
    if (!validateAccount()) return;
    setIsLoading(true);

    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email: formData.email,
      password: formData.password,
      options: { emailRedirectTo: `${window.location.origin}/provider` },
    });

    if (signUpError) {
      toast({ title: "Registration failed", description: signUpError.message, variant: "destructive" });
      setIsLoading(false);
      return;
    }

    const newUserId = signUpData.user?.id;
    if (!newUserId) {
      toast({ title: "Error", description: "Could not create your account.", variant: "destructive" });
      setIsLoading(false);
      return;
    }

    setUserId(newUserId);

    if (signUpData.session) {
      // Auto-confirmed — save profile directly
      await supabase.from("profiles").update({
        first_name: formData.firstName,
        last_name: formData.lastName,
      }).eq("user_id", newUserId);
      setPendingConfirmation(false);
    } else {
      // Email confirmation required — we'll use edge functions for subsequent steps
      setPendingConfirmation(true);
      toast({
        title: "Email verification required",
        description: "Continue filling in your credentials — we'll save everything securely.",
      });
    }

    setIsLoading(false);
    setCurrentStep(1);
  };

  const handleCredentialsStep = async () => {
    if (!validateCredentials() || !userId) return;
    setIsLoading(true);

    if (pendingConfirmation) {
      // Save via edge function (service role) for unconfirmed users
      await supabase.functions.invoke("save-provider-credentials", {
        body: {
          user_id: userId,
          first_name: formData.firstName,
          last_name: formData.lastName,
          npi_number: formData.npiNumber,
          medical_license_number: formData.licenseNumber,
          license_state: formData.licenseState,
          license_expiry: formData.licenseExpiry,
          dea_number: formData.deaNumber || null,
          dea_expiry: formData.deaExpiry || null,
          specialty: formData.specialty || null,
          bio: formData.bio || null,
        },
      });
    } else {
      await supabase.from("provider_credentials" as any).insert({
        user_id: userId,
        npi_number: formData.npiNumber,
        medical_license_number: formData.licenseNumber,
        license_state: formData.licenseState,
        license_expiry: formData.licenseExpiry,
        dea_number: formData.deaNumber || null,
        dea_expiry: formData.deaExpiry || null,
        specialty: formData.specialty || null,
        bio: formData.bio || null,
      });
    }

    // Auto-verify NPI
    setNpiVerifying(true);
    try {
      const { data } = await supabase.functions.invoke("verify-npi", {
        body: { npi_number: formData.npiNumber },
      });
      setNpiResult(data);
      setNpiVerified(data?.verified === true);
      if (data?.verified) {
        toast({ title: "NPI Verified!", description: `Found: ${data.provider_name}` });
      } else {
        toast({ title: "NPI could not be verified", description: "You can re-verify from your dashboard later.", variant: "destructive" });
      }
    } catch {
      toast({ title: "NPI verification unavailable", description: "You can verify later from your dashboard." });
    }
    setNpiVerifying(false);

    setIsLoading(false);
    setCurrentStep(2);
  };

  const handleBaaAccept = async () => {
    if (!userId) return;
    setBaaLoading(true);

    if (pendingConfirmation) {
      await supabase.functions.invoke("save-provider-credentials", {
        body: {
          user_id: userId,
          first_name: formData.firstName,
          last_name: formData.lastName,
          npi_number: formData.npiNumber,
          medical_license_number: formData.licenseNumber,
          license_state: formData.licenseState,
          license_expiry: formData.licenseExpiry,
          baa_accepted_at: new Date().toISOString(),
          baa_ip_address: "client",
        },
      });
    } else {
      await supabase.from("provider_credentials" as any).update({
        baa_accepted_at: new Date().toISOString(),
        baa_ip_address: "client",
      }).eq("user_id", userId);
    }

    setBaaAccepted(true);
    setBaaLoading(false);
    toast({ title: "BAA Signed" });
    setCurrentStep(3);
  };

  const submitRoleRequestAndNotify = async () => {
    if (!userId) return;
    const { data: roleReqData } = await supabase.functions.invoke("create-role-request", {
      body: {
        user_id: userId,
        requested_role: "provider",
        metadata: {
          first_name: formData.firstName,
          last_name: formData.lastName,
          npi_number: formData.npiNumber,
          license_state: formData.licenseState,
          specialty: formData.specialty || null,
        },
      },
    });
    const requestId = roleReqData?.id || crypto.randomUUID();

    // Notify admins via email
    const { data: adminEmails } = await supabase.rpc("get_user_emails");
    const { data: adminRoles } = await supabase.from("user_roles").select("user_id").eq("role", "admin");
    const adminUserIds = new Set((adminRoles || []).map((r) => r.user_id));
    const admins = (adminEmails || []).filter((u) => adminUserIds.has(u.user_id));

    for (const admin of admins) {
      supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "new-role-request",
          recipientEmail: admin.email,
          idempotencyKey: `role-request-${requestId}-${admin.user_id}`,
          templateData: {
            applicantName: `${formData.firstName} ${formData.lastName}`,
            applicantEmail: formData.email,
            requestedRole: "provider",
          },
        },
      });
    }
  };

  const handleW9Upload = async () => {
    if (!w9File || !userId) return;
    setIsLoading(true);

    const filePath = `${userId}/w9-${Date.now()}.${w9File.name.split(".").pop()}`;
    const { error: uploadError } = await supabase.storage.from("w9-uploads").upload(filePath, w9File);
    if (uploadError) {
      toast({ title: "Upload failed", description: uploadError.message, variant: "destructive" });
      setIsLoading(false);
      return;
    }

    await supabase.from("contractor_tax_info").upsert({
      user_id: userId,
      w9_file_url: filePath,
      w9_collected_at: new Date().toISOString(),
      filing_status: "w9_uploaded",
    }, { onConflict: "user_id" });

    await supabase.from("provider_credentials" as any).update({
      w9_status: "uploaded",
    }).eq("user_id", userId);

    await submitRoleRequestAndNotify();
    setIsLoading(false);
    setSubmitted(true);
  };

  const handleSkipW9 = async () => {
    await submitRoleRequestAndNotify();
    toast({ title: "W-9 Skipped", description: "You can upload your W-9 from your dashboard later." });
    setSubmitted(true);
  };

  const progressPercent = Math.round(((currentStep + (submitted ? 1 : 0)) / STEPS.length) * 100);

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="w-full max-w-md text-center space-y-6">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
            <CheckCircle2 size={32} className="text-primary" />
          </div>
          <h1 className="font-display text-3xl text-foreground">Application Submitted</h1>
          {pendingConfirmation ? (
            <>
              <p className="text-muted-foreground">
                Your credentials have been saved. Please <strong>check your email</strong> to verify your account, then log in to access your provider dashboard.
              </p>
              <Link to="/login">
                <Button>Go to Login</Button>
              </Link>
            </>
          ) : (
            <>
              <p className="text-muted-foreground">
                Your provider application is under review. You'll be notified once an administrator has verified your credentials.
              </p>
              <Link to="/provider">
                <Button>Go to Provider Dashboard</Button>
              </Link>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-md space-y-6">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft size={16} /> Back to home
        </Link>

        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Stethoscope size={20} className="text-primary" />
          </div>
          <div>
            <h1 className="font-display text-3xl text-foreground">Provider Registration</h1>
            <p className="text-muted-foreground mt-0.5">Step {currentStep + 1} of {STEPS.length}: {STEPS[currentStep]}</p>
          </div>
        </div>

        {/* Progress indicator */}
        <div className="space-y-2">
          <div className="flex justify-between text-xs text-muted-foreground">
            {STEPS.map((step, i) => (
              <span key={step} className={i <= currentStep ? "text-primary font-medium" : ""}>{step}</span>
            ))}
          </div>
          <Progress value={progressPercent} className="h-2" />
        </div>

        {/* Step 1: Account */}
        {currentStep === 0 && (
          <Card className="border-border bg-card">
            <CardContent className="p-6">
              <form onSubmit={(e) => { e.preventDefault(); handleAccountStep(); }} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First name</Label>
                    <Input id="firstName" value={formData.firstName} onChange={(e) => updateField("firstName", e.target.value)} required />
                    {errors.firstName && <p className="text-sm text-destructive">{errors.firstName}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last name</Label>
                    <Input id="lastName" value={formData.lastName} onChange={(e) => updateField("lastName", e.target.value)} required />
                    {errors.lastName && <p className="text-sm text-destructive">{errors.lastName}</p>}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" placeholder="you@clinic.com" value={formData.email} onChange={(e) => updateField("email", e.target.value)} required />
                  {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input id="password" type={showPassword ? "text" : "password"} value={formData.password} onChange={(e) => updateField("password", e.target.value)} required />
                    <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
                </div>
                <Button type="submit" className="w-full gap-2" disabled={isLoading}>
                  {isLoading ? "Creating account..." : <>Continue <ArrowRight size={14} /></>}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Credentials */}
        {currentStep === 1 && (
          <Card className="border-border bg-card">
            <CardContent className="p-6">
              <form onSubmit={(e) => { e.preventDefault(); handleCredentialsStep(); }} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="npiNumber">NPI Number</Label>
                  <Input id="npiNumber" placeholder="10-digit NPI" maxLength={10} value={formData.npiNumber} onChange={(e) => updateField("npiNumber", e.target.value.replace(/\D/g, ""))} required />
                  {errors.npiNumber && <p className="text-sm text-destructive">{errors.npiNumber}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="licenseNumber">Medical License Number</Label>
                  <Input id="licenseNumber" value={formData.licenseNumber} onChange={(e) => updateField("licenseNumber", e.target.value)} required />
                  {errors.licenseNumber && <p className="text-sm text-destructive">{errors.licenseNumber}</p>}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>License State</Label>
                    <Select value={formData.licenseState} onValueChange={(v) => updateField("licenseState", v)}>
                      <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
                      <SelectContent>
                        {US_STATES.map((s) => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.licenseState && <p className="text-sm text-destructive">{errors.licenseState}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="licenseExpiry">License Expiry</Label>
                    <Input id="licenseExpiry" placeholder="MM/DD/YYYY" value={isoToDisplay(formData.licenseExpiry) || formData.licenseExpiry} onChange={(e) => handleDateField("licenseExpiry", e.target.value)} maxLength={10} required />
                    {errors.licenseExpiry && <p className="text-sm text-destructive">{errors.licenseExpiry}</p>}
                  </div>
                </div>
                {/* Specialty & Bio */}
                <div className="space-y-4 pt-2 border-t border-border">
                  <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider pt-2">Professional Profile <span className="text-xs font-normal normal-case">(optional)</span></p>
                  <div className="space-y-2">
                    <Label htmlFor="specialty">Specialty</Label>
                    <Input id="specialty" placeholder="e.g. Internal Medicine, Endocrinology" value={formData.specialty} onChange={(e) => updateField("specialty", e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bio">Bio</Label>
                    <textarea
                      id="bio"
                      placeholder="Brief professional bio visible to patients"
                      value={formData.bio}
                      onChange={(e) => updateField("bio", e.target.value)}
                      rows={3}
                      maxLength={500}
                      className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                    <p className="text-xs text-muted-foreground text-right">{formData.bio.length}/500</p>
                  </div>
                </div>
                {/* DEA */}
                <div className="space-y-4 pt-2 border-t border-border">
                  <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider pt-2">DEA Registration <span className="text-xs font-normal normal-case">(optional)</span></p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="deaNumber">DEA Number</Label>
                      <Input id="deaNumber" value={formData.deaNumber} onChange={(e) => updateField("deaNumber", e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="deaExpiry">DEA Expiry</Label>
                      <Input id="deaExpiry" placeholder="MM/DD/YYYY" value={isoToDisplay(formData.deaExpiry) || formData.deaExpiry} onChange={(e) => handleDateField("deaExpiry", e.target.value)} maxLength={10} />
                      {errors.deaExpiry && <p className="text-sm text-destructive">{errors.deaExpiry}</p>}
                    </div>
                  </div>
                </div>
                <Button type="submit" className="w-full gap-2" disabled={isLoading || npiVerifying}>
                  {npiVerifying ? "Verifying NPI..." : isLoading ? "Saving..." : <>Continue <ArrowRight size={14} /></>}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 3: BAA */}
        {currentStep === 2 && (
          <BAAgreement onAccept={handleBaaAccept} isLoading={baaLoading} accepted={baaAccepted} />
        )}

        {/* Step 4: W-9 */}
        {currentStep === 3 && (
          <Card className="border-border bg-card">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-2">
                <Upload size={18} className="text-primary" />
                <h3 className="font-display text-lg text-foreground">Upload W-9 Form</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                We need your W-9 for tax reporting (1099). You can also upload this later from your dashboard.
              </p>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setW9File(e.target.files?.[0] || null)}
                className="text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 file:cursor-pointer"
              />
              <div className="flex gap-3">
                <Button onClick={handleW9Upload} disabled={!w9File || isLoading} className="flex-1 gap-2">
                  {isLoading ? "Uploading..." : <>Upload & Submit <ArrowRight size={14} /></>}
                </Button>
                <Button variant="outline" onClick={handleSkipW9} disabled={isLoading}>
                  Skip for Now
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link to="/login" className="text-primary hover:underline font-medium">Log in</Link>
        </p>
      </div>
    </div>
  );
};

export default ProviderRegister;
